import type { BookingRecord, TripStatus } from '../../../types'
import { formatBookingDate, formatBookingTime, formatEuro } from '../../../utils/format'
import { BOOKINGS_GRID } from './bookingsTableLayout'
import { CustomerCell } from './cells/CustomerCell'
import { RouteCell } from './cells/RouteCell'
import { VehicleCell } from './cells/VehicleCell'
import { RowActionsMenu, type RowAction } from '../../../components/ui/RowActionsMenu'
import { PaymentBadge } from './PaymentBadge'
import { TripStatusSelect } from './TripStatusSelect'

type BookingRowProps = {
  booking: BookingRecord
  onTripStatusChange: (id: string, status: TripStatus) => void
  onAssignDriver: (id: string) => void
  actions: RowAction[]
}

export const BookingRow = ({
  booking,
  onTripStatusChange,
  onAssignDriver,
  actions,
}: BookingRowProps) => {
  const rowLabel = `${booking.reference} — ${booking.customer.name}`

  return (
    <div
      className="grid min-h-[60px] items-center justify-between"
      style={{ gridTemplateColumns: BOOKINGS_GRID }}
    >
      <CustomerCell customer={booking.customer} />

      <span className="whitespace-nowrap px-[4px] font-host text-[12px] font-normal leading-normal text-black">
        {booking.reference.length > 12
          ? `${booking.reference.slice(0, 12)}...`
          : booking.reference}
      </span>

      <RouteCell route={booking.route} service={booking.service} />

      <div className="flex flex-col items-start gap-[4px] whitespace-nowrap font-host text-[14px] leading-normal">
        <span className="font-medium text-black">{formatBookingDate(booking.scheduledAt)}</span>
        <span className="font-normal text-dash-cell-muted">{formatBookingTime(booking.scheduledAt)}</span>
      </div>

      <VehicleCell
        driverAssigned={booking.driverAssigned}
        driverName={booking.driverName}
        onAssignDriver={() => onAssignDriver(booking.id)}
        vehicle={booking.vehicle}
      />

      <span className="px-[4px] font-host text-[16px] font-semibold leading-normal text-black">
        {formatEuro(booking.amountEur)}
      </span>

      <PaymentBadge status={booking.payment} />

      <TripStatusSelect
        bookingLabel={rowLabel}
        onChange={(status) => onTripStatusChange(booking.id, status)}
        status={booking.trip}
      />

      <RowActionsMenu actions={actions} subject={rowLabel} />
    </div>
  )
}
